import axios, { type InternalAxiosRequestConfig } from 'axios';
import { clearAuth } from './auth';

const apiClient = axios.create({
  // 同源部署（nginx 反代 /api → backend:8000）时使用相对路径；
  // 开发态由 vite.config.ts 的 server.proxy 把 /api 转到后端
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // 携带 httpOnly cookie（access_token / refresh_token）
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
);

// 标记是否正在刷新 token
let isRefreshing = false;
// 401 排队等待刷新的请求：必须持有原始请求配置，刷新成功后逐个重发。
// （历史上这里只 resolve 一个字符串 'refreshed'：排队请求从不重发，
//   调用方拿到的响应是字符串 → data.data 抛 TypeError，mutation 必失败）
type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };
let failedQueue: Array<{
  request: RetriableRequestConfig;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}> = [];

// 响应拦截器：401 尝试刷新 token，失败则跳转登录
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // 排除 login/refresh 自身避免死循环
      const isAuthRequest =
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/refresh');

      if (isAuthRequest) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ request: originalRequest, resolve, reject });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // 通过 cookie 中的 refresh_token 刷新（后端自动读取 httpOnly cookie）
        await axios.post(
          `${apiClient.defaults.baseURL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        // 刷新成功：重发排队中的原始请求，结果回传给各自的调用方。
        // 重发前打上 _retry：若刷新后仍 401（token 真失效），
        // 不再进入刷新分支造成循环，直接走 reject 链
        failedQueue.forEach(({ request, resolve }) => {
          request._retry = true;
          resolve(apiClient(request));
        });
        failedQueue = [];
        return apiClient(originalRequest);
      } catch (refreshError) {
        failedQueue.forEach(({ reject }) => reject(refreshError));
        failedQueue = [];
        clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
